import React from 'react';
import { keyframes } from '@mui/material/styles';
import { colorMap } from '../../utils/validationUtils';
import { IValidationErrorByExtraction } from '../../types/validation-types';
import { VALIDATING_TOOLTIP, VALIDATION_NOT_READY_TOOLTIP } from '../hooks/useValidationFetchState';

interface Props {
  extractionId?: string;
  validationResponse: IValidationErrorByExtraction | undefined;
  cypressId?: string;
  // The extraction's validation is being refetched: the last result is stale.
  isValidating?: boolean;
  // The extraction's validation poll budget ran out; a retry is offered elsewhere.
  isNotReady?: boolean;
}

const pulse = keyframes({
  '0%': { opacity: 1 },
  '50%': { opacity: 0.25 },
  '100%': { opacity: 1 },
});

const ValidationStatusBadge: React.FC<Props> = ({
  validationResponse,
  cypressId,
  isValidating = false,
  isNotReady = false,
}) => {
  // Filter out dismissed errors (errors with dismissalReason set)
  const nonDismissedErrors = validationResponse?.validationErrorDetails?.filter(
    (error) => !error.dismissalReason
  ) || [];

  const hasRequiredError = nonDismissedErrors.some(
    (error) => error.type === 'required'
  );

  const hasNonRequiredError = nonDismissedErrors.some(
    (error) => error.type !== 'required'
  );

  const commonStyles: React.CSSProperties = {
    height: '5px',
    width: '5px',
    display: 'inline-block',
    borderRadius: '50%',
    marginRight: '2px',
    verticalAlign: 'middle',
  };

  if (isValidating) {
    return (
      <span
        title={VALIDATING_TOOLTIP}
        style={{
          ...commonStyles,
          backgroundColor: colorMap.standard.inactive.backgroundColor,
          animation: `${pulse} 1s ease-in-out infinite`,
        }}
        data-cy={`badge-${cypressId}-validating`}
      ></span>
    );
  }

  if (isNotReady) {
    return (
      <span
        title={VALIDATION_NOT_READY_TOOLTIP}
        style={{
          ...commonStyles,
          backgroundColor: 'transparent',
          boxSizing: 'border-box',
          border: `1px solid ${colorMap.standard.missing.color}`,
        }}
        data-cy={`badge-${cypressId}-not-ready`}
      ></span>
    );
  }

  if (!validationResponse) {
    return (
      <span
        style={{ ...commonStyles, backgroundColor: colorMap.standard.inactive.backgroundColor }}
        data-cy={`badge-${cypressId}-inactive`}
      ></span>
    );
  }

  if (!hasRequiredError && !hasNonRequiredError) {
    return (
      <span
        style={{ ...commonStyles, backgroundColor: 'transparent' }}
        data-cy={`badge-${cypressId}-valid`}
      ></span>
    );
  }

  return (
    <>
      {hasRequiredError && (
        <span
          style={{ ...commonStyles, backgroundColor: colorMap.standard.missing.color }}
          data-cy={`badge-${cypressId}-required`}
        ></span>
      )}
      {hasNonRequiredError && (
        <span
          style={{ ...commonStyles, backgroundColor: colorMap.standard.invalid.color }}
          data-cy={`badge-${cypressId}-error`}
        ></span>
      )}
    </>
  );
};

export default ValidationStatusBadge;
